<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL, ACTION } from '@constants/Text'
import { ICONS } from '@modules/atendimento/constants/Text'
import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import SearchContactList from '@modules/atendimento/components/search-contact-list/search-contact-list.vue'

export default {
  name: 'ModalNovaConversa',
  components: { SearchContactList },
  props: {
    // API + chaves GlobalBus — default atendimento; solicitacoes injeta as suas.
    api: {
      type: Object,
      default: () => apiAtendimento,
    },
    openKey: {
      type: String,
      default: MODAL.ATENDIMENTO_NOVA_CONVERSA,
    },
    criadaKey: {
      type: String,
      default: ACTION.ATENDIMENTO_CONVERSA_CRIADA,
    },
  },
  data() {
    return {
      icons: ICONS,
      busModal: new Vue(),
      loading: false,
      loadingSearch: false,
      loadingSync: false,
      loadingConexoes: false,
      conexoes: [],
      selectedConexao: null,
      searchQuery: '',
      searchResults: [],
      selectedContato: null,
      manualTelefone: '',
      manualNome: '',
      searchTimer: null,
      syncDone: false,
      modoOrigem: 'contato', // 'contato' | 'manual' | 'cliente-base'
      selectedClienteId: null,
      loadingClientes: false,
    }
  },
  computed: {
    conexaoOptions() {
      return this.conexoes
        .filter((c) => c.status === 'conectado')
        .map((c) => ({
          text: c.nome || 'Conexão #' + c.id,
          value: c.id,
        }))
    },
    modoOrigemOptions() {
      return [
        { label: 'Contato da conexão', value: 'contato' },
        { label: 'Inserir manualmente', value: 'manual' },
        { label: 'Cliente da base', value: 'cliente-base' },
      ]
    },
    clientsRaw() {
      return this.$store.getters['user/allClients'] || []
    },
    clientsOptions() {
      return this.clientsRaw.map((c) => {
        const nome = c.nome_cliente || c.name || 'Cliente #' + c.id
        const tel = this._extractTelefone(c)
        return {
          text: tel ? `${nome} - ${tel}` : nome,
          value: c.id,
        }
      })
    },
    selectedCliente() {
      if (!this.selectedClienteId) return null
      return (
        this.clientsRaw.find((c) => c.id === this.selectedClienteId) || null
      )
    },
    // Só renderiza o selector de conexão quando há 2+ opções. Com uma
    // única conexão disponível, o auto-select em open() já resolve e
    // mostrar um dropdown com um item só vira ruído visual.
    showConexaoSelector() {
      return this.conexaoOptions.length > 1
    },
    canCreate() {
      if (!this.selectedConexao) return false
      if (this.modoOrigem === 'contato') return !!this.selectedContato
      if (this.modoOrigem === 'manual')
        return !!(
          this.manualTelefone && this.manualTelefone.trim().length >= 10
        )
      if (this.modoOrigem === 'cliente-base') {
        const c = this.selectedCliente
        return !!(c && this._extractTelefone(c))
      }
      return false
    },
  },
  watch: {
    selectedConexao(newVal) {
      if (newVal) {
        this.syncContatos(newVal)
      }
    },
    modoOrigem(newVal) {
      if (newVal !== 'contato') this.clearSelectedContato()
      if (newVal !== 'manual') {
        this.manualTelefone = ''
        this.manualNome = ''
      }
      if (newVal !== 'cliente-base') this.selectedClienteId = null
      if (newVal === 'cliente-base') this.loadClientes()
    },
  },
  created() {
    GlobalBus.$on(this.openKey, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(this.openKey, this.open)
    this.clearSearchTimer()
  },
  methods: {
    clear() {
      this.conexoes = []
      this.selectedConexao = null
      this.searchQuery = ''
      this.searchResults = []
      this.selectedContato = null
      this.manualTelefone = ''
      this.manualNome = ''
      this.syncDone = false
      this.modoOrigem = 'contato'
      this.selectedClienteId = null
      this.clearLoadingStates()
      this.clearSearchTimer()
    },

    clearLoadingStates() {
      this.loading = false
      this.loadingSearch = false
      this.loadingSync = false
      this.loadingConexoes = false
      this.loadingClientes = false
    },

    // Sempre força refresh ao entrar no modo 'cliente-base' — o usuário pode
    // ter editado o telefone do cliente/lead numa outra aba do CRM e precisa
    // que o valor atualizado venha para a nova conversa.
    async loadClientes() {
      this.loadingClientes = true
      try {
        await this.$store.dispatch('user/getAllClients')
      } finally {
        this.loadingClientes = false
      }
    },

    _extractTelefone(cliente) {
      if (!cliente) return ''
      return (cliente.telefone || cliente.celular || cliente.whatsapp || '')
        .toString()
        .trim()
    },

    async loadConexoes() {
      if (this.loadingConexoes) return
      this.loadingConexoes = true
      try {
        const r = await this.api.listarConexoes()
        if (r && r.status === 200) {
          this.conexoes = r.value || []
        }
      } finally {
        this.loadingConexoes = false
      }
    },

    async open() {
      this.clear()
      this.busModal.$emit('dialog', true)
      await this.loadConexoes()
      const conectadas = this.conexoes.filter((c) => c.status === 'conectado')
      if (conectadas.length === 1) {
        this.selectedConexao = conectadas[0].id
      }
    },

    async syncContatos(id_conexao) {
      this.loadingSync = true
      this.syncDone = false
      try {
        await this.api.syncContatos({ id_conexao })
        this.syncDone = true
      } finally {
        this.loadingSync = false
      }
    },

    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    },

    onSearchInput(value) {
      this.searchQuery = value
      this.selectedContato = null
      this.clearSearchTimer()

      if (!value || value.length < 2) {
        this.searchResults = []
        return
      }

      this.searchTimer = setTimeout(() => {
        this.doSearch()
      }, 300)
    },

    async doSearch() {
      if (!this.searchQuery || this.searchQuery.length < 1) return
      this.loadingSearch = true
      try {
        const r = await this.api.buscarContatos({ q: this.searchQuery })
        if (r && r.status === 200) {
          this.searchResults = r.value || []
        } else {
          this.searchResults = []
        }
      } catch {
        // Falha de rede/timeout na busca de contatos: UX preserva o estado
        // anterior do usuário mostrando a lista vazia sem quebrar o modal.
        this.searchResults = []
      } finally {
        this.loadingSearch = false
      }
    },

    selectContato(contato) {
      this.selectedContato = contato
      this.manualTelefone = ''
      this.manualNome = ''
    },

    clearSelectedContato() {
      this.selectedContato = null
      this.searchResults = []
      this.searchQuery = ''
    },

    _resolveTelefoneNome() {
      if (this.modoOrigem === 'contato' && this.selectedContato) {
        return {
          telefone: this.selectedContato.telefone,
          nome: this.selectedContato.nome,
        }
      }
      if (this.modoOrigem === 'manual') {
        return {
          telefone: this.manualTelefone.trim(),
          nome: this.manualNome.trim() || '',
        }
      }
      if (this.modoOrigem === 'cliente-base') {
        const cliente = this.selectedCliente || {}
        return {
          telefone: this._extractTelefone(this.selectedCliente),
          nome: cliente.nome_cliente || cliente.name || '',
        }
      }
      return { telefone: '', nome: '' }
    },

    async create() {
      if (this.loading) return
      if (!this.canCreate) return

      this.loading = true
      try {
        const { telefone, nome } = this._resolveTelefoneNome()

        const r = await this.api.novaConversa({
          id_conexao: this.selectedConexao,
          telefone,
          nome,
        })

        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
        if (r && r.status === 200) {
          this.busModal.$emit('dialog', false)
          GlobalBus.$emit(this.criadaKey, r.value)
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <BaseModal
    centerScreen
    :bus="busModal"
    persistent
    centerAndDecreasesPadding
    min-width="700px"
    closeColor="#8e8e8e"
    backgroundColor="white"
  >
    <div class="pb-6">
      <div class="modal-title-container">
        <span class="modal-title">Nova Conversa</span>
      </div>

      <div class="modal-content">
        <div class="pa-4">
          <div class="contact-search-block pa-4 rounded-md">
            <!-- Connection selector: só aparece com 2+ conexões disponíveis.
                 Com uma única conexão o auto-select já cuida de tudo, então
                 o dropdown some pra reduzir ruído visual. -->
            <input-by-type
              v-if="showConexaoSelector"
              v-model="selectedConexao"
              type="autocomplete"
              :options="conexaoOptions"
              labelExternal="Conexão*"
              placeholder="Selecione uma conexão..."
              class="align-icon-search mb-4"
              dense
              hide-details
              data-test="conexao-select"
            />

            <!-- Sync indicator -->
            <div v-if="loadingSync" class="d-flex justify-center align-center">
              <v-progress-circular
                indeterminate
                size="16"
                width="2"
                color="primary"
                class="mr-2"
              />
              <span class="az-text-small">
                Sincronizando contatos do WhatsApp..
              </span>
            </div>

            <div
              v-else-if="syncDone"
              class="d-flex justify-center align-center"
            >
              <icon-svg
                :name="icons.checkCircle"
                size="16"
                color="success"
                class="mr-1"
              />
              <span class="az-text-small">Contatos sincronizados</span>
            </div>
          </div>

          <div
            v-if="selectedConexao"
            class="az-card-no-shadow no-padding no-border mt-4 pa-4 pt-0"
          >
            <v-radio-group
              v-model="modoOrigem"
              row
              dense
              hide-details
              class="mt-0 pt-0 modo-origem-radio-group"
              data-test="modo-origem-radio"
            >
              <v-radio
                v-for="opt in modoOrigemOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
                :data-test="'modo-origem-' + opt.value"
              />
            </v-radio-group>

            <!-- Modo: Contato da conexão -->
            <div v-if="modoOrigem === 'contato'" class="modo-origem-content">
              <SearchContactList
                :items="searchResults"
                :selected="selectedContato"
                :loading="loadingSearch"
                label="Buscar contato"
                placeholder="Nome ou telefone (mínimo 2 caracteres)"
                data-test="search-contato"
                @search="onSearchInput"
                @select="selectContato"
                @clear="clearSelectedContato"
              />
            </div>

            <!-- Modo: Inserir manualmente -->
            <div v-if="modoOrigem === 'manual'" class="modo-origem-content">
              <v-row no-gutters class="ma-0">
                <v-col cols="7" class="pa-0 pr-2">
                  <input-by-type
                    v-model="manualTelefone"
                    labelExternal="Telefone*"
                    placeholder="+5511999887766"
                    class="align-icon-search"
                    dense
                    outlined
                    hide-details
                    data-test="manual-telefone"
                    style="min-width: 175px"
                  />
                </v-col>
                <v-col cols="5" class="pa-0">
                  <input-by-type
                    v-model="manualNome"
                    labelExternal="Nome"
                    class="align-icon-search"
                    dense
                    outlined
                    hide-details
                    placeholder="João..."
                    data-test="manual-nome"
                    style="min-width: 175px"
                  />
                </v-col>
                <v-col cols="12" class="pa-0 mt-2">
                  <span class="az-text-small">* Campos obrigatórios</span>
                </v-col>
              </v-row>
            </div>

            <div
              v-if="modoOrigem === 'cliente-base'"
              class="modo-origem-content"
            >
              <input-by-type
                v-model="selectedClienteId"
                type="autocomplete"
                :options="clientsOptions"
                labelExternal="Cliente*"
                placeholder="Selecione um cliente da base..."
                :loading="loadingClientes"
                class="align-icon-search"
                dense
                hide-details
                data-test="cliente-base-select"
              />
              <div
                v-if="selectedCliente && !_extractTelefone(selectedCliente)"
                class="az-text-small error--text mt-2"
                data-test="cliente-base-sem-telefone-hint"
              >
                Este cliente não possui telefone cadastrado. Selecione outro ou
                use "Inserir manualmente".
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <BaseButton underlined @click="close"> Cancelar </BaseButton>
      <BaseButton
        primary
        :loading="loading"
        :disabled="loading || !canCreate"
        @click="create"
      >
        Iniciar Conversa
      </BaseButton>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
// Token local mantido como SASS var — não há equivalente direto em :root
// (CSS vars). Mesma estratégia de chat-message.vue:508-517.
$contact-search-block-bg: #F0F1F3  // cinza claro pro bloco de busca de contato

.contact-search-block
  background-color: $contact-search-block-bg

// Radios sempre numa única linha; labels não quebram mesmo em largura apertada.
.modo-origem-radio-group
  ::v-deep .v-input--radio-group__input
    flex-wrap: nowrap
    white-space: nowrap

  ::v-deep .v-radio
    flex-shrink: 0

    .v-label
      white-space: nowrap

// Distância fixa entre os radios e o conteúdo do modo selecionado,
// independente do modo ativo.
.modo-origem-content
  margin-top: 16px
</style>
